import React, { Fragment, useEffect, useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { hot } from 'react-hot-loader';
import SanitizedHTML from 'sharedWebpack/SanitizedHTML';
import httpClient from 'agentHTTPClient';
import ContentLoaderPlaceholder from '@wtag/rcl-content-loader-placeholder';
import Alert from '@wtag/rcl-alert';
import routes from 'agentRoutes';
import Trip from './Trip';
import RouteInfoWithPrice from './RouteInfoWithPrice';
import TripTitle from './TripTitle';
import capitalizeWord from '../../../../admin/Invoices/Show/helpers/capitalizeWord';
import './style.scss';

const FlightItem = ({
  item,
  className,
  flightServicesByResultId,
  journeyElementId,
  journeyElementType,
  showMinimalInfo,
  showFlightSeatsStatus,
  fetchedJourneyElement,
  isJourneyElementLoading,
  refetchJourneyElement,
  totalTravelerCount,
  payLater,
  laymanMode,
  shouldLockScroll,
  setShouldLockScroll,
  isRouteInfoVisible,
  currency,
  grossAmount,
  supplierReservationIdentifier,
  isPostBooking,
  canExchange,
  pastJourneyElement,
  exchangeFareBrandMap,
}) => {
  const [flightJourneyElement, setflightJourneyElement] = useState(fetchedJourneyElement);
  const [isLoading, setIsLoading] = useState(isJourneyElementLoading);

  const fetchJourneyElement = async () => {
    setIsLoading(true);
    const { data } = await httpClient.get(
      routes.api.fetchJourneyElement({
        type: journeyElementType,
        id: journeyElementId,
      }),
    );

    setflightJourneyElement(data);
    setIsLoading(false);
  };

  useEffect(() => {
    setflightJourneyElement(fetchedJourneyElement);
  }, [fetchedJourneyElement]);

  useEffect(() => {
    setIsLoading(isJourneyElementLoading);
  }, [isJourneyElementLoading]);

  useEffect(() => {
    if (journeyElementId && journeyElementType && refetchJourneyElement) {
      fetchJourneyElement();
    }
  }, [journeyElementId, journeyElementType, refetchJourneyElement]);

  const getUnchangedTrip = () => {
    if (
      !pastJourneyElement?.trips ||
      !flightJourneyElement?.trips ||
      pastJourneyElement.trips.length === flightJourneyElement.trips.length
    ) {
      return null;
    }

    const [firstPastTrip, lastPastTrip] = pastJourneyElement.trips;
    const [currentFirstTrip] = flightJourneyElement.trips;

    const isFirstTripChanged = firstPastTrip.originCode === currentFirstTrip.originCode;
    const unchangedTrip = isFirstTripChanged ? lastPastTrip : firstPastTrip;

    return unchangedTrip;
  };

  const unchangedTrip = getUnchangedTrip();

  return (
    <div className={classNames('flight-item d-flex direction-column row-gap-16', className)}>
      {isLoading ? (
        <ContentLoaderPlaceholder numberOfLines={2} showBackground={true} />
      ) : (
        flightJourneyElement?.trips && (
          <Fragment>
            {isRouteInfoVisible && (
              <RouteInfoWithPrice
                trips={flightJourneyElement.trips}
                currency={currency}
                grossAmount={grossAmount}
                supplierReservationIdentifier={supplierReservationIdentifier}
              />
            )}
            {flightJourneyElement.trips.map((trip, index) => {
              const pastTrip = pastJourneyElement?.trips?.find(
                ({ originCode }) => originCode === trip.originCode,
              );
              const isPartialExchange =
                flightJourneyElement.trips.length !== pastJourneyElement?.trips?.length;

              const selectedFareBrand = trip.segments[0]?.fareBrand?.toLowerCase() || '';
              const quoteFareBrand = exchangeFareBrandMap[trip.id]?.toLowerCase() || '';
              const hasFareBrandChanged =
                canExchange &&
                selectedFareBrand &&
                quoteFareBrand &&
                selectedFareBrand !== quoteFareBrand;

              return (
                <Fragment>
                  <Alert isVisible={hasFareBrandChanged} type="warning-light" hideClose={true}>
                    <div>
                      <SanitizedHTML
                        html={I18n.t('components.ibe.exchange.cart.fare_brand_changed', {
                          selectedFareBrand: capitalizeWord(selectedFareBrand),
                          quoteFareBrand: capitalizeWord(quoteFareBrand),
                        })}
                      />
                    </div>
                  </Alert>
                  <div
                    key={trip.id}
                    className="flight-item-accordion-wrapper d-flex-column row-gap-8"
                  >
                    {canExchange && (
                      <TripTitle
                        originCode={trip.originCode}
                        destinationCode={trip.destinationCode}
                        tripCount={isPartialExchange ? null : index + 1}
                      />
                    )}
                    <Trip
                      item={item}
                      flightServicesByResultId={flightServicesByResultId}
                      showFlightSeatsStatus={showFlightSeatsStatus}
                      tripList={flightJourneyElement.trips}
                      trip={trip}
                      key={trip.id}
                      index={index}
                      flightServices={flightJourneyElement.services}
                      showMinimalInfo={showMinimalInfo}
                      totalTravelerCount={totalTravelerCount}
                      payLater={payLater}
                      laymanMode={laymanMode}
                      shouldLockScroll={shouldLockScroll}
                      setShouldLockScroll={setShouldLockScroll}
                      isPostBooking={isPostBooking}
                      canExchange={canExchange}
                      pastJourneyElement={pastJourneyElement}
                    />
                    {canExchange && pastTrip && (
                      <Trip
                        className="exchange-past-trip"
                        showFlightSeatsStatus={showFlightSeatsStatus}
                        tripList={pastJourneyElement.trips}
                        trip={pastTrip}
                        key={pastTrip.id}
                        flightServices={pastJourneyElement.services}
                        totalTravelerCount={totalTravelerCount}
                        laymanMode={laymanMode}
                        canExchange={canExchange}
                        isDisabled={true}
                      />
                    )}
                  </div>
                </Fragment>
              );
            })}
            {canExchange && unchangedTrip && (
              <div className="flight-item-accordion-wrapper flight-item-accordion-wrapper__unchanged d-flex-column row-gap-8">
                <TripTitle
                  originCode={unchangedTrip.originCode}
                  destinationCode={unchangedTrip.destinationCode}
                  isChanged={false}
                />
                <Trip
                  showFlightSeatsStatus={showFlightSeatsStatus}
                  tripList={pastJourneyElement.trips}
                  trip={unchangedTrip}
                  key={unchangedTrip.id}
                  flightServices={pastJourneyElement.services}
                  totalTravelerCount={totalTravelerCount}
                  laymanMode={laymanMode}
                  canExchange={canExchange}
                />
              </div>
            )}
          </Fragment>
        )
      )}
    </div>
  );
};

FlightItem.defaultProps = {
  className: null,
  showMinimalInfo: false,
  fetchedJourneyElement: null,
  refetchJourneyElement: false,
  showFlightSeatsStatus: false,
  payLater: false,
  isRouteInfoVisible: false,
  supplierReservationIdentifier: '',
  isPostBooking: false,
  canExchange: false,
  pastJourneyElement: {},
  exchangeFareBrandMap: {},
  currency: '',
  grossAmount: '',
};

FlightItem.propTypes = {
  item: PropTypes.shape({}).isRequired,
  className: PropTypes.string,
  flightServicesByResultId: PropTypes.shape({}).isRequired,
  journeyElementId: PropTypes.number.isRequired,
  journeyElementType: PropTypes.string.isRequired,
  showMinimalInfo: PropTypes.bool,
  fetchedJourneyElement: PropTypes.shape({}),
  refetchJourneyElement: PropTypes.bool,
  showFlightSeatsStatus: PropTypes.bool,
  totalTravelerCount: PropTypes.number.isRequired,
  payLater: PropTypes.bool,
  laymanMode: PropTypes.bool.isRequired,
  shouldLockScroll: PropTypes.bool.isRequired,
  setShouldLockScroll: PropTypes.func.isRequired,
  isRouteInfoVisible: PropTypes.bool,
  currency: PropTypes.string,
  grossAmount: PropTypes.string,
  supplierReservationIdentifier: PropTypes.string,
  isPostBooking: PropTypes.bool,
  canExchange: PropTypes.bool,
  pastJourneyElement: PropTypes.shape({
    trips: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        originCode: PropTypes.string,
        destinationCode: PropTypes.string,
      }),
    ),
    services: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  isJourneyElementLoading: PropTypes.bool.isRequired,
  exchangeFareBrandMap: PropTypes.shape({
    [PropTypes.number]: PropTypes.string,
  }),
};
export default hot(module)(FlightItem);
